// ./src/utils/planGating.ts
//
// ⭐ WHICH SHAPE OF GATE A FEATURE GETS — the declaration half of plan gating (ROADMAP A2.10).
//
// `useFeatureGate` answers "is this allowed?". This file answers the OTHER question, the one
// that used to be a judgement call on every page: "when it is refused, what should the host
// see?" There are only two honest answers and they are not interchangeable —
//
//   'page' → the whole page becomes an upgrade screen  (<PremiumFeatureGate>)
//   'add'  → the page works, only "add another" is refused  (<PlanFeatureNotice>)
//   'none' → this feature never gates a page at all
//
// ⚠ BOTH WRONG ANSWERS HURT, IN OPPOSITE DIRECTIONS, which is exactly why it is declared once
// in the API's `appFeatures.ts` and generated here rather than decided per page:
//   • a page lock over a create-gated feature hides records the server is still serving and
//     blocks edits it still accepts — a downgraded host opens a page they have used for months
//     and finds an upgrade screen over their own data;
//   • an add-only notice over a feature the account does not have at all renders a complete,
//     working page for something unusable, with the refusal deferred to the first save.
//
// So adding a feature means declaring its gate ONCE (the API's `AppFeatureDef.gate` is a
// REQUIRED field — `tsc` refuses a feature nobody stated one for), and a page then picks the
// component the declaration names rather than choosing for itself.
import {
  DEFAULT_FEATURE_GATE,
  FEATURE_GATE_KINDS,
  FeatureGateKind,
} from "@/config/featureCodes.generated";

export type { FeatureGateKind };

/**
 * The declared gate kind for a feature, with an optional per-page override.
 *
 * @param code     the feature code
 * @param override a page's deliberate departure from the declaration. ⚠ It is a PROP, not a
 *                 derivation: a page may say "here this one is a whole-page lock", but it may
 *                 not compute the answer from a plan value — that is the re-derivation this
 *                 whole item exists to remove.
 *
 * ⚠ AN UNKNOWN CODE IS NOT "ungated". It is UNDECLARED, which is a different fact, and the two
 * must not collapse: answering 'none' would silently drop the gate for a feature somebody just
 * added, and nothing would error. It answers `DEFAULT_FEATURE_GATE` ('add') instead — the
 * direction that cannot hide data, since the page still works and the save is still refused
 * with the server's own sentence.
 */
export const featureGateKind = (code: string, override?: FeatureGateKind | null): FeatureGateKind => {
  if (override) {
    return override;
  }

  return FEATURE_GATE_KINDS[code] ?? DEFAULT_FEATURE_GATE;
};

/** True when this feature's refusal should replace the whole page. */
export const isPageGated = (code: string, override?: FeatureGateKind | null): boolean => {
  return featureGateKind(code, override) === "page";
};

/** True when this feature's refusal should only block "add another". */
export const isAddGated = (code: string, override?: FeatureGateKind | null): boolean => {
  return featureGateKind(code, override) === "add";
};

/**
 * Whether a feature may be gated in the UI at all.
 *
 * ⚠ 'none' is a real, load-bearing answer, not an absence. It covers three different things,
 * and gating any of them would be a lie to the customer:
 *   • internal plumbing with no page (fee percentages, storage quota, the refund rule);
 *   • credit wallets and hand-delivered services — bought outright, so an upgrade screen would
 *     be selling a subscription for something a subscription does not include;
 *   • capabilities whose surface is not built yet (`insights`, `automation`, `byo_sender`),
 *     which have NO server gate either — a lock there would make the UI the only thing
 *     refusing, while the API kept serving every call the screen was hiding.
 */
export const isGateable = (code: string, override?: FeatureGateKind | null): boolean => {
  return featureGateKind(code, override) !== "none";
};

/**
 * Dev-time complaint when a page reaches for the wrong gate component.
 *
 * ⚠ This is the thing that makes the declaration REAL. Without it `FEATURE_GATE_KINDS` is a
 * table nothing consults and every page goes on choosing for itself — which is the state
 * A2.10 exists to end. It is a console error rather than a throw on purpose: a mismatched
 * gate is a design bug, not a reason to white-screen a host's page. `npm run check-entitlement`
 * catches the same thing statically, before it can ship.
 */
export const warnOnGateMismatch = (
  component: "PremiumFeatureGate" | "PlanFeatureNotice",
  code: string,
  override?: FeatureGateKind | null
): void => {
  if (import.meta.env.PROD) {
    return;
  }

  const kind = featureGateKind(code, override);
  const wanted = component === "PremiumFeatureGate" ? "page" : "add";

  if (kind === wanted) {
    return;
  }

  if (kind === "none") {
    console.error(
      `[planGating] <${component}> is gating '${code}', which is declared gate: 'none' — it is not something a plan includes, so there is nothing to upgrade to. Fix the page, or change the declaration in eventsmagic-api src/boundary/appFeatures.ts.`
    );

    return;
  }

  console.error(
    `[planGating] <${component}> is gating '${code}', but it is declared gate: '${kind}'. Use <${
      kind === "page" ? "PremiumFeatureGate" : "PlanFeatureNotice"
    }>, or pass an explicit gate override if this page really is the exception.`
  );
};
